import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageCareerPlanBool,
  useCanViewCareerPlanBool,
  useRequireCapability
} from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { GET_CAREER_PLAN_QUERY } from "../queries";
import { getCurrentCareerIntent } from "./careerPlanHistory";

export function useCareerProfileQuery() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerPlan });
  const { pgaId } = useParams();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const canManageCareerPlan = useCanManageCareerPlanBool();
  const careerTargetsEnabled = flags["pga-career-targets"] === true;

  const { data, loading, error, refetch } = useQuery(GET_CAREER_PLAN_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !flagsLoaded || !capabilitiesLoaded || !careerTargetsEnabled || !canViewCareerPlan
  });

  const careerIntents = useMemo(
    () => (data?.person?.careerIntents ?? []).filter((goal) => Boolean(goal?.id && goal?.createdAt)),
    [data?.person?.careerIntents]
  );
  const assessment = data?.person?.experienceIndexAssessment ?? null;
  const currentGoal = getCurrentCareerIntent(data?.person?.careerIntent, careerIntents);
  const roleRubrics = data?.careerRoleRubrics ?? [];
  const competencies = data?.competencies ?? [];
  const memberClass = data?.person?.latestProgram?.band?.classification?.name ?? null;
  const employmentRedirect =
    flagsLoaded && !careerTargetsEnabled && pgaId ? `/people/${pgaId}/career/employment` : null;

  return {
    pgaId,
    flagsLoaded,
    capabilitiesLoaded,
    employmentRedirect,
    loading,
    error,
    careerIntents,
    assessment,
    currentGoal,
    roleRubrics,
    competencies,
    memberClass,
    canManageCareerPlan,
    refetch
  };
}

export type CareerProfileQueryResult = ReturnType<typeof useCareerProfileQuery>;
